import { authService } from '@/features/auth/api/auth';
import type { User } from '@/types/user';
import { AuthTokenService } from './auth-token-service';
import { authLogger } from './logger';
import {
  setTokenValidationState,
  setAutoLoginState,
  initializeInterceptors,
} from './interceptors';
import { AuthErrorHandler } from './auth-error-handler';
import { profileService } from '@/shared/api/services/profile.service';
import { ApiClientError } from '@/shared/lib/api';
export interface AuthInitializationResult {
  isAuthenticated: boolean;
  isInitialized: boolean;
  token: string | null;
  user: User | null;
  pendingEmailVerification?: boolean;
  error?: string;
}

export class AuthInitializer {
  private static hasAttemptedAutoLogin = false;
  private static interceptorsInitialized = false;

  /**
   * Reset internal state - useful for testing
   * Allows tests to reset auto-login attempt flag between test runs
   */
  static resetForTesting(): void {
    this.hasAttemptedAutoLogin = false;
    this.interceptorsInitialized = false;
    authLogger.debug('AuthInitializer state reset for testing');
  }

  static async initialize(): Promise<AuthInitializationResult> {
    try {
      // Initialize interceptors once
      if (!this.interceptorsInitialized && typeof window !== 'undefined') {
        authLogger.debug('Initializing interceptors from AuthInitializer...');
        initializeInterceptors();
        this.interceptorsInitialized = true;
      }

      // Step 1: Get token from localStorage (mobile parity — single source).
      const token = await AuthTokenService.getToken();

      // Step 2: If we have a token, validate it with server.
      // Sanctum tokens are opaque — only the server knows if they're expired.
      if (token) {
        return await this.validateToken(token);
      }

      // Step 3: No token, check for dev auto-login.
      return await this.handleNoToken();
    } catch (error) {
      authLogger.error('Auth initialization error:', error);

      // Use unified error handler
      AuthErrorHandler.handleAuthError(error, {
        context: 'token_validation',
        isValidating: true,
      });

      return {
        isAuthenticated: false,
        isInitialized: true,
        token: null,
        user: null,
        error: 'Failed to initialize authentication',
      };
    }
  }

  private static async handleNoToken(): Promise<AuthInitializationResult> {
    authLogger.debug('No token found');

    // Check for dev auto-login
    if (this.shouldAutoLogin()) {
      return await this.performAutoLogin();
    }

    return {
      isAuthenticated: false,
      isInitialized: true,
      token: null,
      user: null,
    };
  }

  private static shouldAutoLogin(): boolean {
    const isDevAutoLoginEnabled =
      process.env.NEXT_PUBLIC_DEV_AUTO_LOGIN === 'true';
    const devEmail = process.env.NEXT_PUBLIC_DEV_AUTO_LOGIN_EMAIL;
    const devPassword = process.env.NEXT_PUBLIC_DEV_AUTO_LOGIN_PASSWORD;

    return !!(
      isDevAutoLoginEnabled &&
      devEmail &&
      devPassword &&
      process.env.NODE_ENV === 'development' &&
      !this.hasAttemptedAutoLogin
    );
  }

  private static async performAutoLogin(): Promise<AuthInitializationResult> {
    this.hasAttemptedAutoLogin = true;
    authLogger.info('Dev auto-login enabled, attempting login...');

    setAutoLoginState(true);

    try {
      const authData = await authService.login({
        email: process.env.NEXT_PUBLIC_DEV_AUTO_LOGIN_EMAIL!,
        password: process.env.NEXT_PUBLIC_DEV_AUTO_LOGIN_PASSWORD!,
        device_name: 'Dev Auto-Login',
      });

      authLogger.info('Dev auto-login successful');

      // Store tokens
      await AuthTokenService.setToken(authData.token);
      await AuthTokenService.setUser(authData.user);

      // Coordination is handled by AuthCoordinator in auth.store

      return {
        isAuthenticated: true,
        isInitialized: true,
        token: authData.token,
        user: authData.user,
      };
    } catch (error) {
      authLogger.warn('Dev auto-login failed:', error);

      // Use unified error handler
      AuthErrorHandler.handleAuthError(error, {
        context: 'login',
        isAutoLogin: true,
      });

      return await this.clearAndReturnUnauthenticated();
    } finally {
      setAutoLoginState(false);
    }
  }

  private static async validateToken(
    token: string
  ): Promise<AuthInitializationResult> {
    authLogger.debug('Validating stored token...');

    setTokenValidationState(true);
    // Token is already set in apiClient via AuthTokenService
    await AuthTokenService.setToken(token);

    try {
      // Fetch profile to validate token
      const profileData = await profileService.fetchProfile();

      // Transform to User type (user should always be defined when fetching profile)
      if (!profileData.user) {
        throw new Error('User data missing from profile response');
      }

      const currentUser: User = {
        id: profileData.user.id,
        email: profileData.user.email,
        email_verified_at: profileData.user.email_verified_at || null,
        created_at: profileData.user.created_at,
        updated_at: profileData.user.updated_at || profileData.user.created_at,
      };

      // Update token storage
      await AuthTokenService.setUser(currentUser);

      // Same shared seeder as login/register/refreshUser — single pipeline
      // for every UserProfileResource payload, so app-boot token validation
      // populates subscription store + token balance identically to a fresh
      // /user refresh. WebSocket takes over balance updates after init.
      await profileService.applyProfileData(profileData, 'auth-init');

      authLogger.debug('Token is valid, user verified');

      return {
        isAuthenticated: true,
        isInitialized: true,
        token,
        user: currentUser,
      };
    } catch (error) {
      authLogger.warn('Token validation failed:', error);

      // Handle 403 (email not verified) — keep token, show verification screen
      if (error instanceof ApiClientError && error.status === 403) {
        authLogger.info(
          'Token valid but email not verified, pending verification'
        );
        return {
          isAuthenticated: true,
          isInitialized: true,
          token,
          user: null,
          pendingEmailVerification: true,
        };
      }

      // Use unified error handler
      AuthErrorHandler.handleAuthError(error, {
        context: 'token_validation',
        clearAuth: true,
      });

      return await this.clearAndReturnUnauthenticated();
    } finally {
      setTokenValidationState(false);
    }
  }

  private static async clearAndReturnUnauthenticated(): Promise<AuthInitializationResult> {
    await AuthTokenService.clearAuth();

    return {
      isAuthenticated: false,
      isInitialized: true,
      token: null,
      user: null,
    };
  }
}
